import React, { useState } from 'react';
import { NICHE_PRESETS } from '../constants/nichePresets';
import { NicheKey } from '../types';
import { 
  Plus, 
  Minus, 
  Check, 
  Zap, 
  Layers, 
  DollarSign, 
  Clock, 
  Package, 
  ChevronRight,
  Info
} from 'lucide-react';
import { formatCurrencyBRL } from '../../../utils/formatUtils';

export interface SelectedNicheItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unitPrice: number;
  total: number;
  isMaterial?: boolean;
  durationMinutes?: number;
}

interface NicheQuickSelectorsProps {
  nicheKey: NicheKey;
  onItemsChange: (items: SelectedNicheItem[]) => void;
  selectedItems: SelectedNicheItem[];
}

export default function NicheQuickSelectors({
  nicheKey,
  onItemsChange,
  selectedItems
}: NicheQuickSelectorsProps) {
  const preset = NICHE_PRESETS[nicheKey] || NICHE_PRESETS.eletricista;
  const [activeCategory, setActiveCategory] = useState<string>(preset.selectorGroups[0]?.id || '');
  const [customItemName, setCustomItemName] = useState('');
  const [customItemPrice, setCustomItemPrice] = useState('');
  const [isAddingCustom, setIsAddingCustom] = useState(false);

  const getItemSelection = (itemId: string) => {
    return selectedItems.find(item => item.id === itemId);
  };

  const handleToggleItem = (categoryTitle: string, item: { id: string; label: string; defaultPrice?: number; isMaterial?: boolean; durationMinutes?: number; defaultQuantity?: number }) => {
    const existing = getItemSelection(item.id);
    if (existing) {
      const updated = selectedItems.filter(i => i.id !== item.id);
      onItemsChange(updated);
    } else {
      const qty = item.defaultQuantity || 1;
      const unitPrice = item.defaultPrice || 0;
      const newItem: SelectedNicheItem = {
        id: item.id,
        name: item.label,
        category: categoryTitle,
        quantity: qty,
        unitPrice: unitPrice,
        total: qty * unitPrice,
        isMaterial: item.isMaterial,
        durationMinutes: item.durationMinutes
      };
      onItemsChange([...selectedItems, newItem]);
    }
  };

  const handleUpdateQuantity = (itemId: string, delta: number) => {
    const updated = selectedItems.map(item => {
      if (item.id === itemId) {
        const newQty = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQty,
          total: newQty * item.unitPrice
        };
      }
      return item;
    });
    onItemsChange(updated);
  };

  const handleAddCustomItem = (categoryTitle: string) => {
    if (!customItemName.trim()) return;
    const price = parseFloat(customItemPrice.replace(',', '.')) || 0;
    const newItem: SelectedNicheItem = {
      id: `custom_${Date.now()}`,
      name: customItemName.trim(),
      category: categoryTitle,
      quantity: 1,
      unitPrice: price,
      total: price
    };
    onItemsChange([...selectedItems, newItem]);
    setCustomItemName('');
    setCustomItemPrice('');
    setIsAddingCustom(false);
  };

  const totalSum = selectedItems.reduce((acc, curr) => acc + curr.total, 0);

  return (
    <div className="w-full bg-white border border-zinc-200/80 rounded-3xl p-3.5 sm:p-6 shadow-sm space-y-4 sm:space-y-6 min-w-0">
      {/* Header com Identificação do Nicho */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-zinc-100 pb-3 sm:pb-4 min-w-0">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
              Seletores Rápidos do Nicho
            </span>
            <span className="text-xs text-zinc-500 font-medium truncate">
              {preset.displayName}
            </span>
          </div>
          <h3 className="text-sm sm:text-lg font-black text-zinc-900 truncate">
            Monte o Escopo em Poucos Toques
          </h3>
        </div>

        {/* Totalizador ao Vivo */}
        <div className="bg-zinc-50 border border-zinc-200 rounded-2xl px-3 py-1.5 sm:px-4 sm:py-2 flex items-center justify-between sm:justify-start gap-3 shrink-0">
          <div>
            <p className="text-[9px] sm:text-[10px] text-zinc-500 font-bold uppercase tracking-wider">Subtotal Itens</p>
            <p className="text-sm sm:text-base font-extrabold text-emerald-700">{formatCurrencyBRL(totalSum)}</p>
          </div>
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-xs">
            {selectedItems.length}
          </div>
        </div>
      </div>

      {/* Navegação por Categorias (Pills) */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none max-w-full">
        {preset.selectorGroups.map(group => {
          const isCurrent = (activeCategory || preset.selectorGroups[0]?.id) === group.id;
          const countSelectedInGroup = selectedItems.filter(i => i.category === group.title).length;

          return (
            <button
              key={group.id}
              type="button"
              onClick={() => setActiveCategory(group.id)}
              className={`px-4 py-2.5 rounded-2xl text-xs font-bold whitespace-nowrap transition-all flex items-center gap-2 cursor-pointer ${
                isCurrent
                  ? 'bg-emerald-700 text-white shadow-sm font-black'
                  : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
              }`}
            >
              <span>{group.title}</span>
              {countSelectedInGroup > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-black ${
                  isCurrent ? 'bg-white text-emerald-800' : 'bg-emerald-600 text-white'
                }`}>
                  {countSelectedInGroup}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Grid de Itens */}
      {preset.selectorGroups
        .filter(group => group.id === (activeCategory || preset.selectorGroups[0]?.id))
        .map(group => (
          <div key={group.id} className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              {group.items.map(item => {
                const selected = getItemSelection(item.id);
                const isSelected = !!selected;

                return (
                  <div
                    key={item.id}
                    onClick={() => handleToggleItem(group.title, item)}
                    className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex flex-col justify-between select-none ${
                      isSelected
                        ? 'bg-emerald-50/60 border-emerald-500 shadow-xs'
                        : 'bg-zinc-50/50 border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-2.5">
                        <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                          isSelected ? 'bg-emerald-600 text-white' : 'border border-zinc-300 bg-white'
                        }`}>
                          {isSelected && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                        </div>
                        <div>
                          <p className={`text-xs font-bold leading-snug ${
                            isSelected ? 'text-zinc-900' : 'text-zinc-700'
                          }`}>
                            {item.label}
                          </p>
                          {item.durationMinutes && (
                            <p className="text-[10px] text-zinc-500 flex items-center gap-1 mt-0.5">
                              <Clock className="h-3 w-3" />
                              <span>{item.durationMinutes} min</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs font-extrabold text-emerald-700 shrink-0">
                        {item.defaultPrice ? formatCurrencyBRL(item.defaultPrice) : '--'}
                      </span>
                    </div>

                    {/* Controles de Quantidade */}
                    {isSelected && (
                      <div 
                        className="mt-3 pt-2.5 border-t border-emerald-200 flex items-center justify-between"
                        onClick={e => e.stopPropagation()}
                      >
                        <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider">
                          Quantidade
                        </span>
                        <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-xl border border-zinc-200">
                          <button
                            type="button"
                            onClick={() => handleUpdateQuantity(item.id, -1)}
                            className="p-1 hover:text-zinc-900 text-zinc-500 rounded transition-colors"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-xs font-black text-zinc-900 px-1">
                            {selected?.quantity || 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleUpdateQuantity(item.id, 1)}
                            className="p-1 hover:text-zinc-900 text-zinc-500 rounded transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Adicionar Item Customizado */}
            {isAddingCustom ? (
              <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-2xl space-y-3">
                <p className="text-xs font-bold text-zinc-800">Novo item personalizado em {group.title}</p>
                <div className="grid sm:grid-cols-[1fr_120px] gap-3">
                  <input
                    type="text"
                    placeholder="Nome do item ou serviço..."
                    value={customItemName}
                    onChange={e => setCustomItemName(e.target.value)}
                    className="bg-white border border-zinc-300 rounded-xl px-3 py-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-emerald-600"
                  />
                  <input
                    type="text"
                    placeholder="Valor (R$)"
                    value={customItemPrice}
                    onChange={e => setCustomItemPrice(e.target.value)}
                    className="bg-white border border-zinc-300 rounded-xl px-3 py-2 text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-emerald-600"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsAddingCustom(false)}
                    className="px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-900"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddCustomItem(group.title)}
                    className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl shadow-xs"
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingCustom(true)}
                className="w-full py-3 border border-dashed border-zinc-300 hover:border-emerald-500 rounded-2xl text-xs font-bold text-zinc-500 hover:text-emerald-700 transition-all flex items-center justify-center gap-2 cursor-pointer bg-white hover:bg-emerald-50/20"
              >
                <Plus className="h-4 w-4" />
                <span>Adicionar outro item em {group.title}</span>
              </button>
            )}
          </div>
        ))}
    </div>
  );
}
